/* eslint-disable react/no-unknown-property */
import React, { useEffect, useState } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'

const Viewer3D = ({
  file,
  mtlFile = null,
  classNames = '',
  onError,
  enableRotation = true,
  enablePan = false,
  enableZoom = true,
  autoRotate = false,
}) => {
  const [scene, setScene] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    setError(null)

    const loadModel = async () => {
      try {
        if (file.endsWith('.obj')) {
          const materials = mtlFile ? await new MTLLoader().loadAsync(mtlFile) : null
          const objLoader = new OBJLoader()

          if (materials) {
            materials.preload()
            objLoader.setMaterials(materials)
          }

          const obj = await objLoader.loadAsync(file)
          setScene(obj)
        } else if (file.endsWith('.gltf') || file.endsWith('.glb')) {
          const gltf = await new GLTFLoader().loadAsync(file)
          setScene(gltf.scene)
        } else if (file.endsWith('.fbx')) {
          const fbx = await new FBXLoader().loadAsync(file)
          setScene(fbx)
        } else {
          throw new Error('Unsupported file format.')
        }
      } catch (err) {
        console.error('Error loading 3D model:', err)
        const errorMessage = 'Failed to load the 3D model.'
        setError(errorMessage)
        if (onError) {
          onError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    loadModel()
  }, [file, mtlFile, onError])

  if (loading) {
    return <></>
  }

  if (error) {
    return <div className="error-message">{error}</div>
  }

  return (
    <div className={classNames}>
      <Canvas
        className="w-full h-full"
        camera={{ position: [0, 0, 5], fov: 45 }}
        style={{ cursor: enableRotation ? 'grab' : 'default' }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[file.endsWith('.fbx') ? 50 : 10, 10, 5]} intensity={0.8} castShadow />
        <pointLight position={[-10, -10, -10]} intensity={0.2} />
        {scene && <primitive object={scene} />}
        <OrbitControls
          enableRotate={enableRotation}
          enablePan={enablePan}
          enableZoom={enableZoom}
          autoRotate={autoRotate}
          autoRotateSpeed={0.5}
          minDistance={1}
          maxDistance={10}
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          dampingFactor={0.05}
          enableDamping={true}
          rotateSpeed={0.8}
          zoomSpeed={0.8}
          panSpeed={0.5}
          screenSpacePanning={false}
          makeDefault
        />
      </Canvas>
    </div>
  )
}

export default Viewer3D
